<script lang="ts">
	import type { Issue, Report, Severity, IssueCategory } from '#lib/types';
	import { i18n, t } from '#lib/i18n/state.svelte';
	import { issueTextEn } from '#lib/i18n/issues';
	import { categoryLabel, severityLabel, severityHint, SEVERITY_UI } from '#lib/ui/labels';
	import IssueCard from './IssueCard.svelte';

	interface Props {
		report: Report;
		onjump: (issue: Issue) => void;
		onselect: (issueId: string) => void;
		expandedId?: string | null;
	}

	let { report, onjump, onselect, expandedId = null }: Props = $props();

	let severity = $state<'all' | Severity>('all');
	let category = $state<'all' | IssueCategory>('all');
	let includeUndecided = $state(true);
	let query = $state('');

	const categories = $derived(
		[...new Set(report.issues.map((i) => i.category))] as IssueCategory[]
	);

	const filtered = $derived(
		report.issues.filter((i) => {
			if (!includeUndecided && !i.decisive) return false;
			if (severity !== 'all' && i.severity !== severity) return false;
			if (category !== 'all' && i.category !== category) return false;
			if (query.trim()) {
				const q = query.toLowerCase();
				const en = i18n.lang === 'en' ? issueTextEn(i) : null;
				const haystack = [i.title, i.detail, i.quote ?? '', en?.title ?? '', en?.detail ?? '']
					.join(' ')
					.toLowerCase();
				if (!haystack.includes(q)) return false;
			}
			return true;
		})
	);

	const severityOptions = $derived([
		{ key: 'all', label: t('findings.all'), count: report.issues.length },
		{
			key: 'critical',
			label: severityLabel('critical'),
			count: report.issues.filter((i) => i.severity === 'critical').length
		},
		{
			key: 'warning',
			label: severityLabel('warning'),
			count: report.issues.filter((i) => i.severity === 'warning').length
		},
		{
			key: 'info',
			label: severityLabel('info'),
			count: report.issues.filter((i) => i.severity === 'info').length
		}
	] as const);

	const undecidedCount = $derived(filtered.filter((i) => !i.decisive).length);
</script>

<div class="findings">
	<div class="controls card">
		<div class="seg" role="group" aria-label={t('severity.label')}>
			{#each severityOptions as o (o.key)}
				<button
					class="seg-btn"
					class:active={severity === o.key}
					onclick={() => (severity = o.key)}
					aria-pressed={severity === o.key}
				>
					{o.label} <span class="count">{o.count}</span>
				</button>
			{/each}
		</div>

		<div class="filters">
			<label class="field">
				<span class="sr-only">{t('findings.category')}</span>
				<select bind:value={category}>
					<option value="all">{t('findings.categoryAll')}</option>
					{#each categories as c (c)}
						<option value={c}>{categoryLabel(c)}</option>
					{/each}
				</select>
			</label>

			<label class="field search">
				<span class="sr-only">{t('findings.searchLabel')}</span>
				<input type="search" placeholder={t('findings.search')} bind:value={query} />
			</label>

			<label class="checkbox">
				<input type="checkbox" bind:checked={includeUndecided} />
				<span>{t('findings.includeUndecided')}</span>
			</label>
		</div>
	</div>

	<div class="result-line">
		{t('findings.showing', { n: filtered.length })}
		{#if undecidedCount > 0}
			<span class="muted">{t('findings.showingUndecided', { n: undecidedCount })}</span>
		{/if}
	</div>

	{#if filtered.length === 0}
		<div class="card empty">
			<p>{t('findings.empty')}</p>
		</div>
	{:else}
		<ul class="list">
			{#each filtered as issue (issue.id)}
				<li>
					<IssueCard
						{issue}
						expanded={expandedId === issue.id}
						ontoggle={(i) => onselect(i.id)}
						onjump={onjump}
					/>
				</li>
			{/each}
		</ul>
	{/if}

	<div class="legend small muted">
		{#each Object.entries(SEVERITY_UI) as [key, v] (key)}
			{#if key !== 'ok'}
				<span class="leg">
					<span class="dot" style:background={v.dot}></span>
					{t('findings.legendText', {
						label: severityLabel(key as Severity),
						hint: severityHint(key as Severity)
					})}
				</span>
			{/if}
		{/each}
	</div>
</div>

<style>
	.findings {
		display: grid;
		gap: 0.85rem;
	}
	.controls {
		padding: 0.85rem 0.95rem;
		display: grid;
		gap: 0.75rem;
	}
	.seg {
		display: inline-flex;
		background: var(--paper-2);
		border: 1px solid var(--line);
		border-radius: 9px;
		padding: 3px;
		gap: 3px;
		justify-self: start;
		flex-wrap: wrap;
	}
	.seg-btn {
		border: 0;
		background: transparent;
		font-size: 0.8rem;
		padding: 0.3rem 0.65rem;
		border-radius: 6px;
		cursor: pointer;
		color: var(--ink-2);
	}
	.seg-btn.active {
		background: #fff;
		box-shadow: var(--shadow-sm);
		font-weight: 600;
		color: var(--ink);
	}
	.count {
		color: var(--muted);
		font-variant-numeric: tabular-nums;
		font-size: 0.74rem;
	}
	.filters {
		display: flex;
		gap: 0.6rem;
		flex-wrap: wrap;
		align-items: center;
	}
	.field select,
	.field input {
		border: 1px solid var(--line-strong);
		background: #fff;
		border-radius: 8px;
		padding: 0.4rem 0.6rem;
		font-size: 0.84rem;
	}
	.search input {
		min-width: 190px;
	}
	.checkbox {
		display: inline-flex;
		align-items: center;
		gap: 0.4rem;
		font-size: 0.8rem;
		color: var(--ink-2);
		cursor: pointer;
	}
	.checkbox input {
		accent-color: var(--accent);
	}
	.result-line {
		font-size: 0.82rem;
		color: var(--ink-2);
	}
	.list {
		list-style: none;
		padding: 0;
		margin: 0;
		display: grid;
		gap: 0.5rem;
	}
	.empty {
		padding: 1.5rem;
		text-align: center;
		color: var(--muted);
	}
	.empty p {
		margin: 0;
	}
	.legend {
		display: grid;
		gap: 0.2rem;
		margin-top: 0.5rem;
	}
	.leg {
		display: flex;
		align-items: center;
		gap: 0.4rem;
	}
	.dot {
		width: 6px;
		height: 6px;
		border-radius: 50%;
		flex: none;
	}
</style>
